import React, { useState, useEffect } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import axios from "axios";
import { baseurl } from "../baseurl";
import Swal from "sweetalert2";
import Select from "react-select";
import { State, City } from 'country-state-city';

function Profile() {
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", business_name: "",
    gst_number: "", website: "", address_line1: "",
    country: "IN", state: "", city: "", postal_code: "", status: ""
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: ""
  });

  const vendorId = localStorage.getItem("vendorId");

  // Fetch Profile Data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${baseurl}getVendorProfileById/${vendorId}`);
        if (res.data.success) {
          const v = res.data.vendor;
          setFormData({ 
            ...v, 
            state: v.state || "", 
            city: v.city || "", 
            country: "IN",
            address_line1: v.address_line1 || ""
          });
        }
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [vendorId]);

  // Update Profile Logic
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Note: Endpoint matching your provided backend logic
      const res = await axios.put(`${baseurl}updateVendorProfile/${vendorId}`, { ...formData, id: vendorId });
      if (res.data.success) {
        Swal.fire({ 
          icon: "success", 
          title: "Profile Updated", 
          toast: true, 
          position: "top-end", 
          timer: 2500, 
          showConfirmButton: false 
        });
      }
    } catch (error) {
      Swal.fire({ icon: "error", title: "Update Failed", text: error.response?.data?.error || "Server error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update Password Logic (Direct Reset)
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return Swal.fire({ icon: "warning", title: "Passwords do not match" });
    }
    try {
      const res = await axios.post(`${baseurl}updateVendorPassword`, { 
        newPassword: passwordData.newPassword, 
        id: vendorId 
      });
      if (res.data.success) {
        Swal.fire({ icon: "success", title: "Password Reset Successful" });
        setPasswordData({ newPassword: "", confirmPassword: "" });
      }
    } catch (error) {
      Swal.fire({ icon: "error", title: "Reset Failed" });
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Profile...</div>;

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <div className="row g-4">
            
            {/* Left Column: Profile Info */}
            <div className="col-lg-8">
              <div className="card profile-card shadow-sm border-0">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h4 className="fw-bold mb-0">Business Profile</h4>
                      <p className="text-muted small mb-0">Update your official identity and location.</p>
                    </div>
                    <span className={`badge ${formData.status === 'active' ? 'bg-success' : 'bg-secondary'} text-uppercase p-2`}>
                      {formData.status || 'Active'}
                    </span>
                  </div>

                  <form onSubmit={handleProfileSubmit}>
                    <div className="section-title text-uppercase">General Information</div>
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Full Name</label>
                        <input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Contact Number</label>
                        <input type="text" className="form-control" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold">Email Address (Read Only)</label>
                        <input type="email" className="form-control bg-light" value={formData.email} readOnly />
                      </div>
                    </div>

                    <div className="section-title text-uppercase">Business Details</div>
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Business Name</label>
                        <input type="text" className="form-control" value={formData.business_name} onChange={(e) => setFormData({...formData, business_name: e.target.value})} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">GST Number</label>
                        <input type="text" className="form-control text-uppercase" value={formData.gst_number} onChange={(e) => setFormData({...formData, gst_number: e.target.value.toUpperCase()})} />
                      </div>
                    </div>

                    <div className="section-title text-uppercase">Location</div>
                    <div className="row g-3 mb-4">
                      <div className="col-12">
                        <label className="form-label fw-semibold">Street Address</label>
                        <input type="text" className="form-control" value={formData.address_line1} onChange={(e) => setFormData({...formData, address_line1: e.target.value})} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">State</label>
                        <Select
                          options={State.getStatesOfCountry("IN").map(s => ({ value: s.isoCode, label: s.name }))}
                          value={State.getStatesOfCountry("IN").filter(s => s.isoCode === formData.state).map(s => ({ value: s.isoCode, label: s.name }))[0]}
                          onChange={(opt) => setFormData({...formData, state: opt.value, city: ""})}
                          placeholder="Select State"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">City</label>
                        <Select
                          options={City.getCitiesOfState("IN", formData.state).map(c => ({ value: c.name, label: c.name }))}
                          value={formData.city ? { label: formData.city, value: formData.city } : null}
                          onChange={(opt) => setFormData({...formData, city: opt.value})}
                          isDisabled={!formData.state}
                          placeholder="Select City"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">Pincode</label>
                        <input type="text" className="form-control" value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} />
                      </div>
                    </div>

                    <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-update shadow-sm">
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Right Column: Security */}
            <div className="col-lg-4">
              <div className="card profile-card shadow-sm mb-4 border-0">
                <div className="card-body p-4">
                  <div className="section-title text-uppercase">Reset Password</div>
                  <p className="text-muted extra-small mb-3" style={{ fontSize: '0.75rem' }}>
                    Updating your password will require you to log in again on other devices.
                  </p>
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">New Password</label>
                      <input type="password" placeholder="••••••••" className="form-control form-control-sm" required 
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Confirm Password</label>
                      <input type="password" placeholder="••••••••" className="form-control form-control-sm" required 
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} />
                    </div>
                    <button type="submit" className="btn btn-dark btn-sm w-100 fw-bold py-2">Update Security</button>
                  </form>
                </div>
              </div>

              {/* Information Alert */}
              <div className="alert info-alert p-3">
                <h6 className="fw-bold mb-2 small"><i className="bi bi-shield-check me-2"></i>Data Privacy</h6>
                <p className="mb-0 text-muted" style={{fontSize: '0.75rem', lineHeight: '1.4'}}>
                  Your business details are used for billing and tax compliance. Ensure your <strong>GST number</strong> matches your registered business name to avoid verification issues.
                </p>
              </div>
            </div>

          </div>
        </div>

        <style>
          {`
            .profile-card { border-radius: 12px; background: #fff; }
            .section-title { font-size: 0.8rem; letter-spacing: 1px; font-weight: 800; color: #4e73df; margin-bottom: 1.2rem; display: flex; align-items: center; }
            .section-title::after { content: ""; flex: 1; height: 1px; background: #eaecf4; margin-left: 10px; }
            .form-label { font-size: 0.8rem; color: #4e4e4e; }
            .form-control { font-size: 0.85rem !important; border-radius: 6px !important; border: 1px solid #d1d3e2; padding: 0.6rem; }
            .form-control:focus { border-color: #4e73df; box-shadow: 0 0 0 0.2rem rgba(78,115,223,0.1); }
            .info-alert { background: #f8f9fc; border-left: 4px solid #4e73df; border-radius: 8px; }
            .btn-update { padding: 10px 35px; font-weight: 600; }
            .extra-small { font-size: 0.75rem; }
            /* React Select styling to match Bootstrap */
            .css-b62m3t-container { font-size: 0.85rem !important; }
          `}
        </style>
      </main>
    </>
  );
}

export default Profile;